import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";

export const CAREER_INTERESTS = [
  "Production Assistant",
  "Producing",
  "Directing",
  "Writing",
  "Camera",
  "Locations",
  "Art Department",
  "Editing",
  "Other",
];

function getSource() {
  const params = new URLSearchParams(window.location.search);
  const s = params.get("source");
  const map = {
    filmscape: "Filmscape",
    qr: "QR Code",
    referral: "Referral",
    website: "Website",
  };
  if (s && map[s.toLowerCase()]) return map[s.toLowerCase()];
  return "Website";
}

export function WaitlistForm({ onSuccess, compact = false }) {
  const [form, setForm] = useState({
    first_name: "",
    email: "",
    career_interest: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.email) {
      toast.error("Please enter your first name and email.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", { ...form, source: getSource() });
      setDone(true);
      toast.success("You're in! Your Resource Guide is ready below.");
      onSuccess?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="flex flex-col items-center text-center gap-4 py-6"
        data-testid="waitlist-success"
      >
        <CheckCircle2 className="h-14 w-14 text-[#C5A059]" />
        <h3 className="font-display text-2xl text-white">You're in!</h3>
        <p className="text-[#94A3B8] max-w-sm">
          Welcome aboard, you're on the waitlist. Download your Free Film Industry Resource Guide
          below and start building your career behind the scenes.
        </p>
        <a href="/resource-guide.pdf" download data-testid="download-kit-btn">
          <Button className="bg-[#C5A059] hover:bg-[#D4AF37] text-[#060D18] font-semibold rounded-full px-8 h-12 transition-all hover:-translate-y-0.5">
            Download Resource Guide
          </Button>
        </a>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="waitlist-form">
      <Input
        data-testid="input-first-name"
        placeholder="First Name"
        value={form.first_name}
        onChange={(e) => update("first_name", e.target.value)}
        className="bg-[#060D18] border-[#C5A059]/20 text-white placeholder:text-[#64748B] h-12 focus-visible:ring-[#C5A059]"
      />
      <Input
        data-testid="input-email"
        type="email"
        placeholder="Email Address"
        value={form.email}
        onChange={(e) => update("email", e.target.value)}
        className="bg-[#060D18] border-[#C5A059]/20 text-white placeholder:text-[#64748B] h-12 focus-visible:ring-[#C5A059]"
      />
      <Select value={form.career_interest} onValueChange={(v) => update("career_interest", v)}>
        <SelectTrigger
          data-testid="select-career-interest"
          className="bg-[#060D18] border-[#C5A059]/20 text-white h-12 focus:ring-[#C5A059] data-[placeholder]:text-[#64748B]"
        >
          <SelectValue placeholder="Career Interest (optional)" />
        </SelectTrigger>
        <SelectContent className="bg-[#0C172A] border-[#C5A059]/20 text-white">
          {CAREER_INTERESTS.map((c) => (
            <SelectItem
              key={c}
              value={c}
              data-testid={`career-option-${c.toLowerCase().replace(/\s+/g, "-")}`}
              className="focus:bg-[#C5A059]/15 focus:text-white"
            >
              {c}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button
        type="submit"
        disabled={loading}
        data-testid="waitlist-submit-btn"
        className="w-full bg-[#C5A059] hover:bg-[#D4AF37] text-[#060D18] font-semibold rounded-full h-12 text-base transition-all hover:-translate-y-0.5"
      >
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Get the Free Resource Guide"}
      </Button>
      <p className="text-xs text-[#64748B] text-center">
        No spam. Unsubscribe anytime. You'll also join the book waitlist.
      </p>
    </form>
  );
}
